<script setup>
// NOTICE: KAPTION-INTEGRATION
// two-step-verification.UI.2 — six single-digit boxes (same look as the
// upstream MfaVerification input). v-model is the joined string; `complete`
// fires once all six digits are in. Paste of a full code fills every box.
import { ref, computed, watch, nextTick } from 'vue';
import { handleOtpPaste } from 'shared/helpers/clipboard';

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  length: { type: Number, default: 6 },
});
const emit = defineEmits(['update:modelValue', 'complete']);

const digits = ref(Array.from({ length: props.length }, () => ''));
const inputs = ref([]);
const labelId = `kpt-code-${Math.random().toString(36).slice(2, 8)}`;

const code = computed(() => digits.value.join(''));

watch(
  () => props.modelValue,
  value => {
    if (value === code.value) return;
    const chars = String(value || '')
      .replace(/\D/g, '')
      .slice(0, props.length)
      .split('');
    digits.value = Array.from(
      { length: props.length },
      (_, i) => chars[i] || ''
    );
  }
);

const focus = i => {
  if (i >= 0 && i < props.length) inputs.value[i]?.focus();
};

const sync = () => {
  emit('update:modelValue', code.value);
  if (code.value.length === props.length) emit('complete', code.value);
};

const onInput = async i => {
  const value = digits.value[i].replace(/\D/g, '');
  digits.value[i] = value.slice(-1);
  if (digits.value[i] && i < props.length - 1) {
    await nextTick();
    focus(i + 1);
  }
  sync();
};

const onBackspace = (event, i) => {
  if (!digits.value[i] && i > 0) {
    event.preventDefault();
    digits.value[i - 1] = '';
    focus(i - 1);
    sync();
  }
};

const onPaste = event => {
  event.preventDefault();
  const pasted = handleOtpPaste(event, props.length);
  if (!pasted) return;
  digits.value = pasted.split('');
  focus(props.length - 1);
  sync();
};

const clear = async () => {
  digits.value = Array.from({ length: props.length }, () => '');
  emit('update:modelValue', '');
  await nextTick();
  focus(0);
};

defineExpose({ focus: () => focus(0), clear });
</script>

<template>
  <div class="flex flex-col gap-2">
    <span
      v-if="label"
      :id="labelId"
      class="text-sm font-medium text-n-slate-12"
    >
      {{ label }}
    </span>
    <div
      role="group"
      :aria-labelledby="label ? labelId : undefined"
      class="flex justify-between gap-2"
    >
      <input
        v-for="(_, i) in digits"
        :key="i"
        ref="inputs"
        v-model="digits[i]"
        type="text"
        inputmode="numeric"
        maxlength="1"
        :autocomplete="i === 0 ? 'one-time-code' : 'off'"
        :aria-label="`${i + 1}`"
        :disabled="disabled"
        data-testid="kpt-two-step-digit"
        class="w-12 h-12 text-lg font-semibold text-center border-2 rounded-lg !mb-0 border-n-weak hover:border-n-strong focus:border-n-brand bg-n-alpha-black2 text-n-slate-12 disabled:opacity-60"
        @input="onInput(i)"
        @keydown.left.prevent="focus(i - 1)"
        @keydown.right.prevent="focus(i + 1)"
        @keydown.backspace="onBackspace($event, i)"
        @paste="onPaste"
      />
    </div>
  </div>
</template>
